import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';

import { DigitalKey, KeyStatus } from '../../../../models/types';

@Component({
  selector: 'app-key-status-banner',
  standalone: true,
  imports: [CommonModule],
  animations: [
    trigger('fadeSlide', [
      transition('* => *', [
        style({ opacity: 0, transform: 'translateY(-5px)' }),
        animate('220ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
  template: `
    @if (statusConfig(); as config) {
      <div
        [@fadeSlide]="effectiveStatus() + '-' + isOnline()"
        [attr.role]="effectiveStatus() === 'expired' ? 'alert' : 'status'"
        class="flex items-center justify-between gap-4 rounded-2xl border px-4.5 py-4 transition-colors duration-300"
        [style.background-color]="config.bg"
        [style.border-color]="config.border"
      >
        <div class="flex min-w-0 items-center gap-3.5">
          <span [style.color]="config.textColor" class="flex shrink-0 items-center justify-center">
            @switch (config.icon) {
              @case ('wifi-off') {
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line x1="2" y1="2" x2="22" y2="22"></line>
                  <path d="M8.5 16.5a5 5 0 0 1 7 0"></path>
                  <path d="M2 8.82a15 15 0 0 1 4.17-2.65"></path>
                  <path d="M10.66 5c4.01-.36 8.14.9 11.34 3.82"></path>
                </svg>
              }
              @case ('refresh-cw') {
                <svg
                  class="animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                  <path d="M3 3v5h5"></path>
                </svg>
              }
              @case ('clock') {
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              }
              @case ('shield-check') {
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                  ></path>
                  <polyline points="9 12 11 14 15 10"></polyline>
                </svg>
              }
              @case ('shield-alert') {
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
                  ></path>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              }
            }
          </span>
          <div class="min-w-0">
            <p class="truncate font-semibold" [style.color]="config.textColor">
              {{ config.text }}
            </p>
            @if (config.sub) {
              <p class="mt-0.5 text-xs font-medium" [style.color]="config.textColor">
                {{ config.sub }}
              </p>
            }
          </div>
        </div>

        @if (effectiveStatus() === 'valid' && !isOnline()) {
          <div
            class="flex shrink-0 items-center gap-1.5 rounded-[14px] px-3.5 py-1.5"
            style="background-color: #FBBF24"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#78350F"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="shrink-0"
            >
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span class="text-base font-semibold" style="color: #78350F">
              {{ countdown() }}
            </span>
          </div>
        }

        @if (showRefreshBtn()) {
          <button
            (click)="refresh.emit()"
            class="flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-transform active:scale-95"
            [style.background-color]="
              'color-mix(in srgb, ' + config.textColor + ' 12%, transparent)'
            "
            [style.border-color]="'color-mix(in srgb, ' + config.textColor + ' 30%, transparent)'"
            [style.color]="config.textColor"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
              <path d="M3 3v5h5"></path>
            </svg>
            {{
              effectiveStatus() === 'expired'
                ? 'Get New Key'
                : effectiveStatus() === 'none'
                  ? 'Fetch Key'
                  : 'Renew'
            }}
          </button>
        }

        @if (effectiveStatus() === 'expired' && !isOnline()) {
          <span
            class="flex shrink-0 items-center gap-1 text-xs font-medium"
            style="color: var(--th-label)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="2" y1="2" x2="22" y2="22"></line>
              <path d="M8.5 16.5a5 5 0 0 1 7 0"></path>
              <path d="M2 8.82a15 15 0 0 1 4.17-2.65"></path>
              <path d="M10.66 5c4.01-.36 8.14.9 11.34 3.82"></path>
            </svg>
            Offline
          </span>
        }
      </div>
    }
  `,
})
export class KeyStatusBannerComponent {
  digitalKey = input<DigitalKey | null>(null);
  keyStatus = input<KeyStatus>('none');
  isOnline = input<boolean>(false);
  readonly nowMs = input<number>(Date.now());

  refresh = output<void>();

  protected readonly effectiveStatus = computed<KeyStatus>(() => {
    const key = this.digitalKey();
    if (this.keyStatus() === 'valid' && key && this.nowMs() >= key.expiresAt) return 'expired';
    return this.keyStatus();
  });

  countdown = computed(() => {
    const key = this.digitalKey();
    if (!key) return '00:00:00';
    return this.formatCountdown(Math.max(0, key.expiresAt - this.nowMs()));
  });

  showRefreshBtn = computed(() => {
    const status = this.effectiveStatus();
    const online = this.isOnline();

    return status === 'none' && online;
  });

  statusConfig = computed(() => {
    const status = this.effectiveStatus();
    const online = this.isOnline();

    switch (status) {
      case 'none':
        return {
          bg: 'color-mix(in srgb, var(--th-label) 8%, transparent)',
          border: 'color-mix(in srgb, var(--th-label) 20%, transparent)',
          iconBg: 'color-mix(in srgb, var(--th-label) 10%, transparent)',
          icon: 'wifi-off',
          text: 'No digital key',
          sub: 'Go online to get one',
          textColor: 'var(--th-label)',
        };
      case 'fetching':
        return {
          bg: 'color-mix(in srgb, var(--th-loading) 8%, transparent)',
          border: 'color-mix(in srgb, var(--th-loading) 25%, transparent)',
          iconBg: 'color-mix(in srgb, var(--th-loading) 10%, transparent)',
          icon: 'refresh-cw',
          text: 'Fetching key…',
          sub: null,
          textColor: 'var(--th-loading)',
        };
      case 'valid': {
        if (!online) {
          return {
            bg: '#FDF4E3',
            border: 'rgba(217, 119, 6, 0.2)',
            iconBg: 'rgba(217, 119, 6, 0.1)',
            icon: 'shield-check',
            text: 'Digital Key Active',
            sub: 'No internet, usable for',
            textColor: '#B45309',
          };
        }
        return {
          bg: 'color-mix(in srgb, var(--th-success) 8%, transparent)',
          border: 'color-mix(in srgb, var(--th-success) 30%, transparent)',
          iconBg: 'color-mix(in srgb, var(--th-success) 10%, transparent)',
          icon: 'shield-check',
          text: 'Digital Key Active',
          sub: 'Connected — key stays active while online',
          textColor: 'var(--th-success-text)',
        };
      }
      case 'expired':
        return {
          bg: 'color-mix(in srgb, var(--th-label) 8%, transparent)',
          border: 'color-mix(in srgb, var(--th-label) 20%, transparent)',
          iconBg: 'color-mix(in srgb, var(--th-label) 10%, transparent)',
          icon: 'shield-alert',
          text: 'Digital key expired',
          sub: 'Offline time limit reached - reconnect to restore access',
          textColor: 'var(--th-label)',
        };
      default:
        return {
          bg: 'transparent',
          border: 'transparent',
          iconBg: 'transparent',
          icon: 'wifi-off',
          text: 'Unknown status',
          sub: null,
          textColor: 'inherit',
        };
    }
  });

  private formatCountdown(ms: number): string {
    if (ms <= 0) return '00:00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
  }
}
